/**
 * ActiveCallIndicator — floating top-right widget that surfaces an in-flight
 * call when the agent has navigated away from the surface that started it.
 *
 * The popup-driven call flow is already self-indicating (the modal follows
 * navigation), so we hide ourselves when the popup is open. The
 * route-driven flow (`/call-center/call-lead/{sessionName}`) doesn't — once
 * the agent navigates out, the call view unmounts and nothing on screen
 * acknowledges the live call. This widget fills that gap and offers the
 * three controls an agent realistically needs while heads-down on other
 * work: mute, end, and a one-click "Return to call" that navigates back to
 * the originating route.
 *
 * Visibility matrix:
 *
 *   isCallActive     | popup open | on originPath | render?
 *   -----------------|------------|---------------|---------
 *   false            | —          | —             | no
 *   true             | true       | —             | no  (popup is the indicator)
 *   true             | false      | true          | no  (call view is on screen)
 *   true             | false      | false         | YES
 */
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Mic, MicOff, PhoneOff, ArrowUpRight } from "lucide-react";
import { SESSION_STATUS_IN_PROGRESS } from "@icebergeradmin/common";
import { useTwilioContext } from "@/context/twilio.context";
import { useCallLeadPopup } from "@/context/call.lead.popup.context";
import { cn } from "@/lib/utils";

const formatDuration = (ms: number) => {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

const ActiveCallIndicator = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    state,
    isCallActive,
    isMuted,
    callStartedAt,
    leadName,
    phoneNumber,
    originPath,
    leadId,
    sessionName,
    mute,
    unmute,
    hangup,
  } = useTwilioContext();
  const { isOpen: popupOpen } = useCallLeadPopup();

  // Tick once a second while in-call so the duration display updates.
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (state !== "in-call") return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [state]);

  if (!isCallActive) return null;
  // Popup is its own persistent indicator — never compete with it.
  if (popupOpen) return null;
  // If the agent is already on the originating route, the call view is on
  // screen and the indicator is redundant.
  if (originPath && location.pathname === originPath) return null;

  const stateLabel =
    state === "in-call"
      ? "ACTIVE CALL"
      : state === "ringing"
        ? "RINGING"
        : "CALLING";

  return (
    <div
      role="region"
      aria-label="Active call"
      className="fixed top-4 right-4 z-40 w-[300px] rounded-xl border border-emerald-500/30 bg-gradient-to-b from-[#1B1F28]/95 to-[#0D1018]/95 shadow-[0_10px_40px_rgba(0,0,0,0.5)] backdrop-blur-md overflow-hidden"
    >
      {/* Top stripe — pulsing dot + state + duration */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-white/5">
        <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold tracking-wider text-emerald-300">
          <span className="relative flex w-1.5 h-1.5">
            <span className="absolute inline-flex w-full h-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
            <span className="relative inline-flex w-1.5 h-1.5 rounded-full bg-emerald-400" />
          </span>
          {stateLabel}
        </span>
        {state === "in-call" && callStartedAt && (
          <span className="text-white/80 font-mono text-xs tabular-nums">
            {formatDuration(now - callStartedAt.getTime())}
          </span>
        )}
      </div>

      {/* Identity */}
      <div className="px-3 py-2">
        <p className="text-sm font-semibold text-white truncate">
          {leadName ?? phoneNumber ?? "Active call"}
        </p>
        {leadName && phoneNumber && (
          <p className="text-xs text-white/50 font-mono truncate">{phoneNumber}</p>
        )}
      </div>

      {/* Controls */}
      <div className="grid grid-cols-3 gap-1 p-2 border-t border-white/5 bg-black/20">
        <button
          type="button"
          onClick={isMuted ? unmute : mute}
          disabled={state !== "in-call"}
          title={isMuted ? "Unmute" : "Mute"}
          className={cn(
            "flex flex-col items-center justify-center gap-0.5 rounded-lg py-2 transition-colors",
            isMuted
              ? "bg-white/15 text-white"
              : "bg-white/5 text-white/80 hover:bg-white/10",
            state !== "in-call" && "opacity-40 cursor-not-allowed hover:bg-white/5",
          )}
        >
          {isMuted ? <MicOff className="w-3.5 h-3.5" /> : <Mic className="w-3.5 h-3.5" />}
          <span className="text-[10px] font-medium">
            {isMuted ? "Unmute" : "Mute"}
          </span>
        </button>

        {originPath ? (
          <button
            type="button"
            onClick={() =>
              navigate(originPath, {
                state:
                  sessionName && leadId !== null
                    ? {
                        lead: {
                          sessionName,
                          leadId,
                          name: leadName ?? phoneNumber ?? "",
                          phoneNumber: phoneNumber ?? undefined,
                          email: "",
                          role: 0 as const,
                          sessionStatus: SESSION_STATUS_IN_PROGRESS,
                          startDate: new Date().toISOString(),
                        },
                      }
                    : undefined,
              })
            }
            title="Return to call screen"
            className="flex flex-col items-center justify-center gap-0.5 rounded-lg py-2 bg-white/5 text-white/80 hover:bg-white/10 transition-colors"
          >
            <ArrowUpRight className="w-3.5 h-3.5" />
            <span className="text-[10px] font-medium">Return</span>
          </button>
        ) : (
          // No origin recorded — fill the slot with a disabled placeholder so
          // the 3-column grid stays aligned. (Shouldn't normally happen since
          // popup-flow calls hide the indicator entirely.)
          <div className="rounded-lg py-2" />
        )}

        <button
          type="button"
          onClick={hangup}
          title="End call"
          className="flex flex-col items-center justify-center gap-0.5 rounded-lg py-2 bg-red-500 text-white hover:bg-red-600 transition-colors"
        >
          <PhoneOff className="w-3.5 h-3.5" />
          <span className="text-[10px] font-medium">End</span>
        </button>
      </div>
    </div>
  );
};

export default ActiveCallIndicator;
